import { Injectable } from '@angular/core';
import { sortBy } from 'lodash-es';
import { Subject, Observable, of } from 'rxjs';
import { catchError, take, filter, map } from 'rxjs/operators';
import { Recording } from 'src/app/recordings/state';
import { SearchRecordings } from 'src/app/recordings/state/recording.model';
import {
  SongApiService,
  SongLink,
  DownloadTask,
  DownloadLink,
  DownloadFile,
  LyricResponse,
  RightResponse,
  RightCreditsResponse,
  ApprovalsResponse,
  WorKNotesResponse,
  SyncNotesResponse,
  AssetNotesResponse,
} from 'src/app/song/types';
import { CurrentUserService } from 'src/app/user/current-user.service';
import { IUser } from 'src/app/user/user.model';
import { DictionaryService } from '../dictionary';
import { Territory } from '../projects';
import { SongStatus } from '../types/songs';
import { SyncApiService } from './sync-api.service';
import { MusimapFiltersQuery } from '../../search/musimap-filters/musimap-services/musimap-filters.query';
import { MUSIMAP_DIVISIONS } from 'src/app/search/musimap-filters/musimap-common/musimap-common.enum';
import { isTrue } from '../../abstract';

@Injectable({
  providedIn: 'root',
})
export class WmgSongsApiService implements SongApiService {
  secondPlay$: Subject<number | string> = new Subject<number | string>();

  constructor(
    private api: SyncApiService,
    private currentUserService: CurrentUserService,
    private dictionaryService: DictionaryService,
    private musimapFiltersQuery: MusimapFiltersQuery
  ) {}

  getTrackLink(id: number, download: boolean = false): Observable<SongLink> {
    return this.api
      .get(`recordings/${id}/link`, {
        download: download ? 'true' : 'false',
      })
      .pipe(
        catchError(error => {
          return of({
            content: null,
            error,
          });
        })
      );
  }

  createDownloadLink(): Observable<DownloadTask> {
    return undefined;
  }

  getDownloadLink(): Observable<DownloadLink> {
    return undefined;
  }

  download(): Observable<DownloadFile> {
    return undefined;
  }

  getWaveform(): [] {
    return [];
  }

  requestSong(playlistId: number, id: number): Observable<any> {
    return this.api
      .put(`projects/${playlistId}/playlist/songs`, {
        recordingId: id,
        recordingStatus: SongStatus.REQUESTED,
      })
      .pipe(
        take(1),
        catchError(() => of({}))
      );
  }

  getLyrics(id: number | string): Observable<any> {
    return this.api.get(`recording/${id}/lyrics`).pipe(
      take(1),
      catchError(() => {
        return 'Lyric error';
      })
    );
  }

  downloadLyricsAsPDF(id: number | string): Observable<any> {
    return this.api.get(`recording/${id}/lyrics-pdf`, {}, {}, 'arraybuffer').pipe(
      catchError(() => {
        return 'Lyric download error';
      })
    );
  }

  downloadWorkLyricsAsPDF(workId: number | string): Observable<any> {
    return this.api.get(`works/${workId}/lyrics-pdf`, {}, {}, 'arraybuffer').pipe(
      catchError(() => {
        return 'Lyric download error';
      })
    );
  }

  refreshWork(workId: number | string): Observable<any> {
    return this.api.get(`works/${workId}/tango-refresh`);
  }

  getEROSDetiails(isrc: string): Observable<any> {
    return this.api.get(`recording/erosc-publisher/${isrc}`);
  }

  getLyricsForWork(workId: number | string): Observable<LyricResponse> {
    return this.api.get<LyricResponse>(`works/${workId}/lyrics`);
  }

  getWorkNotesForWork(workId: number | string): Observable<WorKNotesResponse[]> {
    return this.api.get<WorKNotesResponse[]>(`works/${workId}/work-notes`);
  }

  getSyncNotesForWork(workId: number | string): Observable<SyncNotesResponse[]> {
    return this.api.get<SyncNotesResponse[]>(`works/${workId}/notes`);
  }

  getTangoSyncNotes(songCode: string | number): Observable<any> {
    return this.api.get(`works/${songCode}/tango-sync-notes`);
  }

  getAssetNotesForRecordings(recordingId: number | string): Observable<AssetNotesResponse> {
    return this.api.get<AssetNotesResponse>(`recording/${recordingId}/recording-note`);
  }

  getRights(songCode: string, territoryId: number): Observable<RightResponse> {
    return this.api.get<RightResponse>(`works/${songCode}/rights/${territoryId}`);
  }

  getRightsCredits(songCode: string, territoryId: number): Observable<RightCreditsResponse> {
    return this.api.get<RightCreditsResponse>(`works/${songCode}/rightscredits/${territoryId}`);
  }

  getApprovals(songCode: string, territoryId: number): Observable<ApprovalsResponse[]> {
    return this.api.get<ApprovalsResponse[]>(`works/${songCode}/approvalsrestrictions/${territoryId}`);
  }

  requestUserInfo(): Observable<IUser> {
    return this.currentUserService.getCurrentUserInfo();
  }

  updateUserAlbumArtFlag(flag: boolean): Observable<any> {
    return this.api.put('songs/search/settings', {
      showAlbumsArt: flag,
    });
  }

  getVersions(song: Recording, limit: number = 100): Observable<SearchRecordings | string> {
    const apiParams = {
      limit,
      offset: 0,
      sort: 'relevance',
      sortOrder: 'desc',
      scope: 'RECORDINGS',
      includeFilters: [],
      excludeFilters: [],
      rangeSearchFilter: [],
      savedFiltersOnly: true,
      includeCoverArtLink: false,
      includeExtendedSearchFilters: false,
      hasAudioFile: true,
      thirdPartyOwned: true,
      divisions: this.musimapFiltersQuery
        .getDivisions()
        .filter(element => element !== MUSIMAP_DIVISIONS.THIRD_PARTY_OWNED),
      clauses: [
        {
          type: 'TITLE',
          queries: [song.title],
        },
        song.artists[0]
          ? {
              type: 'ARTIST',
              queries: [song.artists[0]],
            }
          : null,
      ].filter(isTrue),
    };

    return this.api.post<SearchRecordings>('songs/search', apiParams).pipe(
      take(1),
      catchError(() => {
        return 'Version api post error';
      })
    );
  }

  getRightsTerritories(sortCriteria: string = 'name'): Observable<Territory[]> {
    return this.dictionaryService.fetchTerritories().pipe(
      filter(territories => !!territories?.length),
      map(territories => sortBy(territories, sortCriteria))
    );
  }
}
